import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MemberForm } from "Bands/components";
import {
  useBandContext,
  useBandInvitationContext,
  usePermissionsContext,
} from "Bands/contexts";
import { RedirectToPermissionsModal } from "Bands/pages/members/RedirectToPermissionsModal";
import { BAND_PATHS } from "Bands/router";
import {
  createBandInvitation,
  dataMetaSelector,
  getBandUsers,
} from "Bands/state";
import { BAND_ACTIONS, IBandInvitationProps } from "Bands/types";
import {
  useAPIError,
  useBandRoute,
  useClearActionMetaOnUnMount,
} from "Bands/utils";
import { copy } from "shared/assets";
import { IAPIState, IFormButtons, IJSONAPIErrorCodes } from "shared/types";
import { API_ENDPOINTS } from "shared/constants";
import { getIdFromPath } from "shared/utils";

const { createMember: createCopy } = copy.members;

interface CreateInvitationFormProps {}

export const CreateBandInvitationForm: FC<CreateInvitationFormProps> = () => {
  const { userCanManageBand } = usePermissionsContext();
  const { bandId } = useBandContext();

  const dispatch = useDispatch();

  const { api: apiMeta, id: invitedBandUserId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createBandInvitation)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.createBandInvitation);

  const { pushRoute, getPath } = useBandRoute();
  const { fetchPendingInvitations } = useBandInvitationContext();
  const [redirectToPermissions, setRedirectToPermissions] = useState(false);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      // refetch pending invitations
      fetchPendingInvitations();

      setRedirectToPermissions(true);
    }
  }, [apiMeta, fetchPendingInvitations]);

  const handleSubmit = (invitation: IBandInvitationProps) => {
    dispatch(createBandInvitation(invitation));
  };

  const handleCloseModal = () => {
    setRedirectToPermissions(false);
    pushRoute(BAND_PATHS.MEMBERS);
  };

  const handleRedirect = () => {
    dispatch(getBandUsers(bandId!));
    setRedirectToPermissions(false);
    pushRoute(BAND_PATHS.EDIT_PERMISSION, {
      params: {
        userId:
          getIdFromPath(invitedBandUserId, API_ENDPOINTS.BAND_USERS) || "",
      },
      state: { from: getPath(BAND_PATHS.MEMBERS) },
    });
  };

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: {
          children: createCopy.buttons.primary,
        },
      } as IFormButtons),
    []
  );

  const { error } = useAPIError({
    apiError: apiMeta?.error,
    message: createCopy.messages.alreadyInvited,
    errorCode: IJSONAPIErrorCodes.NotUniqueError,
  });

  return (
    <>
      {redirectToPermissions && (
        <RedirectToPermissionsModal
          handleRedirect={handleRedirect}
          handleClose={handleCloseModal}
        />
      )}

      <MemberForm
        apiStatus={apiMeta?.status}
        handleSubmit={handleSubmit}
        buttons={buttons}
        error={error}
        apiError={apiMeta?.error}
        canManageMemberCategories={userCanManageBand}
      />
    </>
  );
};
